import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { TensionLightingSection } from "@/components/TensionLightingSection";
import hero from "@/assets/sail-shade-blue-underside.jpg";
import heroPicture from "@/assets/sail-shade-blue-underside.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import sailBefore from "@/assets/sail-shade-before-red.jpg";
import sailAfter from "@/assets/sail-shade-after-black.jpg";
import pavilion from "@/assets/clear-vinyl-pavilion-enclosure-golf-course.jpg";
import dropCurtain from "@/assets/clear-vinyl-drop-curtains-pavilion-enclosure.jpg";
import sanMarket from "@/assets/san-market-green-stripe-awning-night.jpg";

export const Route = createFileRoute("/church-shade-structures")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Church Shade Structures — Courtyard, Playground & Entry Canopies | Texas Made Shade",
      description:
        "Custom shade structures for Texas churches — courtyard sails, children's playground shade, entry canopies.",
      image: hero,
      imageAlt: "Tensioned shade sail underside — Texas Made Shade church and community shade structures",
      socialTitle: "Church Shade Structures — Texas Made Shade",
      socialDescription:
        "Shade sails, entry canopies and pavilion enclosures for Texas churches and ministries.",
      keywords:
        "church shade structures, church courtyard shade, church playground shade sail, church entry canopy, church pavilion enclosure, drop curtains church pavilion, Texas church shade, ministry shade structures",
    }),
    scripts: serviceHeadScripts({
      path: "/church-shade-structures",
      serviceName: "Church Shade Structures",
      breadcrumbName: "Church Shade Structures",
      description:
        "Custom shade sails, entry canopies, courtyard covers and pavilion enclosures for Texas churches and ministries — fabricated in Seguin, TX.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/church-shade-structures"
      serviceName="Church Shade Structures"
      breadcrumbs={[{ label: "Church Shade Structures" }]}
      whyUsPlacement="afterHero"
      eyebrow="★ CHURCH SHADE STRUCTURES ★"
      title="Church Shade Structures — Courtyards, Playgrounds & Entry Canopies"
      intro="Texas Made Shade builds shade sails, entry canopies, fellowship-hall patio covers and pavilion drop curtains for churches across Central, South and Coastal Texas. Engineered for full sun, wind and rain — quiet on Sunday morning, durable through Texas summers."
      heroImage={heroPicture}
      features={[
        "Tensioned shade sails for courtyards and gathering areas",
        "Entry canopies and drop-off / portico covers",
        "Children's ministry & playground shade",
        "Fellowship hall patio covers",
        "Clear-vinyl pavilion drop curtains for year-round use",
        "Engineered welded posts — designed for Texas wind",
      ]}
      body={[
        { heading: "Courtyard & Gathering Shade Sails", text: "Tensioned shade sails over fellowship courtyards and outdoor gathering areas cut surface temps and let your congregation linger after service through Texas summer." },
        { heading: "Entry & Portico Canopies", text: "Drop-off canopies over the main entry keep guests dry between car and door — engineered welded steel posts, branded canvas or shade fabric in your colors." },
        { heading: "Children's Ministry & Playground Shade", text: "Shade sails over children's playground equipment protect kids from UV and reduce surface temps — sized to standard play structures.", image: { src: sailAfter, alt: "Black tensioned shade sail installed over a residential play area — same build used for church playgrounds" } },
        { heading: "Fellowship Hall Patios & Pavilions", text: "Patio canopies and pavilion enclosures with clear-vinyl drop curtains let your fellowship hall serve year-round — open in spring, enclosed in winter.", image: { src: pavilion, alt: "Clear-vinyl pavilion enclosure on a golf course — same drop-curtain system used for church pavilions" } },
        { heading: "Clear-Vinyl Drop Curtains", text: "Roll-down clear vinyl curtains turn an open pavilion into a usable winter space — perfect for outdoor fellowship and event venues.", image: { src: dropCurtain, alt: "Clear-vinyl drop curtains installed on an outdoor pavilion enclosure" } },
        { heading: "Branded Entry Awnings & Banners", text: "Hand-painted or printed entry banners and awnings over the main door — branded for your ministry and built for Texas weather.", image: { src: sanMarket, alt: "Branded storefront awning at night — same build style for church entry banners" } },
      ]}
      applicationsEyebrow="WHERE WE INSTALL"
      applicationsHeading="Church Shade Applications"
      applications={[
        "Courtyards",
        "Entry Porticos",
        "Children's Playgrounds",
        "Fellowship Hall Patios",
        "Outdoor Pavilions",
        "Drop-Off / Pickup Loops",
        "Memorial Gardens",
        "Outdoor Classrooms",
        "Coffee Areas",
        "Event Tents",
      ]}
      faqs={[
        { q: "Do you work with church facilities and ministries on shade structures?", a: "Yes — we work directly with church facility teams, building committees and ministry leadership across Texas. PO-ready quotes, scheduled installs around service times, and engineered designs for your site." },
        { q: "Before-and-after of a real shade-sail install?", a: "Yes — we keep before/after photos of every project. Our gallery shows the same site before and after the engineered sail goes in, including post placement and tension hardware." },
        { q: "Can a pavilion be enclosed for year-round use?", a: "Yes — clear-vinyl roll-down curtains turn an open pavilion into a usable winter space. We install around the existing pavilion structure with no permanent walls." },
        { q: "How are shade sails engineered for Texas wind?", a: "Posts and brackets are engineered to the local wind load of the install site, with welded steel posts and stainless tension hardware. Fabric is high-tenacity Serge Ferrari® or Phifertex® rated for full Texas exposure." },
      ]}
      gallerySection={{
        eyebrow: "Church / Ministry Work",
        title: "Recent Church & Pavilion Installs",
        images: [
          { src: sailBefore, alt: "Before — old red residential awning prior to shade-sail upgrade" },
          { src: sailAfter, alt: "After — new tensioned black shade sail installed at the same Texas property" },
          { src: pavilion, alt: "Clear-vinyl pavilion enclosure system — used for church and event pavilions" },
          { src: dropCurtain, alt: "Roll-down clear-vinyl drop curtains for outdoor pavilion enclosure" },
          { src: hero, alt: "Underside of tensioned blue sail shade — Texas Made Shade engineered shade structure" },
          { src: sanMarket, alt: "Branded entry awning — same hand-painted canvas style used for church entry banners" },
        ],
      }}
    >
    <TensionLightingSection />
    </ServicePage>
  ),
});
